import { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

import {
  UploadPmpMonitoringReportFailStage,
  type UploadPmpMonitoringReportResponse,
} from "service/reportCenter/types";
import styles from "./responseMsg.module.scss";
import errorIcon from "../../assets/error.svg";
import successIcon from "../../assets/success.svg";
import copyIcon from "../../assets/copy.svg";
import { message } from "antd";

interface ResponseMsgProps {
  response: UploadPmpMonitoringReportResponse;
}

const escapeHtml = (str: string) =>
  str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const ResponseMsg = ({ response }: ResponseMsgProps) => {
  const { messages = [], success, failStage } = response;

  // Only the rows scrolled into view are mounted, so a list of thousands of
  // errors no longer renders thousands of <li> nodes at once.
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 24, // line-height; corrected per row by measureElement
    overscan: 8,
  });

  const handleCopyAll = async () => {
    if (!messages.length) return;

    // Build the clipboard payload directly from data — O(n) string work, no DOM
    // selection / reflow / synchronous serialization (the source of the freeze on
    // large message lists).
    const plainText = messages.join("\n");
    const htmlText = `<ul>${messages.map((m) => `<li>${escapeHtml(m)}</li>`).join("")}</ul>`;

    try {
      if (navigator.clipboard && typeof window.ClipboardItem !== "undefined") {
        // Async write of both text/plain and text/html keeps formatting and
        // does not block the main thread.
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/plain": new Blob([plainText], { type: "text/plain" }),
            "text/html": new Blob([htmlText], { type: "text/html" }),
          }),
        ]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plainText);
      } else {
        // Last-resort fallback for insecure contexts: copy plain text via a
        // detached textarea (no rendered-list selection, so still cheap).
        const textarea = document.createElement("textarea");
        textarea.value = plainText;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);
        if (!ok) throw new Error("Copy command failed");
      }
      message.success("All error messages copied to clipboard!");
    } catch (err) {
      message.error("Failed to copy error messages. Please try copying manually.");
    }
  };

  return (
    <div className={styles.container}>
      <img
        src={success ? successIcon : errorIcon}
        alt={success ? "Success" : "Error"}
        className={styles.icon}
        data-testid="upload-result-icon"
      />
      {failStage !== UploadPmpMonitoringReportFailStage.DataError ? (
        <p className={styles.singleMessage} data-testid="upload-result-single-text">
          {messages.map((msg, index) => (
            <span key={index} className={styles.messageText}>
              {msg}
            </span>
          ))}
        </p>
      ) : (
        <div className={styles.errorSection}>
          <div className={styles.copyAllRow}>
            <button
              className={styles.copyAllBtn}
              onClick={handleCopyAll}
              data-testid="copy-all-btn"
            >
              <img src={copyIcon} alt="Copy All" />
              <span>Copy All</span>
            </button>
          </div>
          <div className={styles.errorBox} ref={scrollRef}>
            <ul
              className={styles.errorList}
              data-testid="upload-result-error-list"
              style={{ height: virtualizer.getTotalSize(), position: "relative" }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => (
                <li
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {messages[virtualRow.index]}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponseMsg;
